/**
 * The questions My task asks of a day: which seven days are on the strip, what
 * each appointment on it is doing right now, and what the four figures across
 * the top come to.
 *
 * Here rather than in the page because the strip, the tiles and the timeline
 * all ask them and must agree — three components each deciding for themselves
 * what "upcoming" means is how a screen ends up counting two upcoming
 * appointments above a list that shows three.
 *
 * Everything about an appointment itself is `appointments.ts`'s and is
 * imported from there. What is here is the day.
 */

import {
  bySoonest,
  hasEnded,
  isInProgress,
} from "../appointments/appointments";
import { isStandingStatus } from "../appointments/types/appointments.types";
import type { ClientAppointment } from "../appointments/types/appointments.types";

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Days on the strip. A week, which is as far as a practitioner plans. */
export const WEEK_LENGTH = 7;

/** Midnight on the day a moment falls in, so two dates compare as days. */
export const atMidnight = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whether two moments fall on the same day, locally. */
export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Whether the day being looked at is behind the one `now` falls in.
 *
 * Compared at midnight, so it is a question about days rather than moments:
 * four o'clock this afternoon is not "past" because it is behind this instant.
 *
 * Here rather than worked out twice because two things turn on it and they
 * have to agree — the strip greys the days that have gone, and an empty one
 * of them says something different from an empty day still ahead. A strip
 * saying a day is over above a panel offering to fill it is the disagreement
 * this prevents.
 */
export const isPastDay = (day: Date, now: Date = new Date()): boolean =>
  atMidnight(day).getTime() < atMidnight(now).getTime();

/**
 * The seven days of the week a date falls in, Monday first.
 *
 * Monday rather than Sunday because that is where a working week starts, which
 * is what this strip is a strip of — and what the design draws. `getDay()`
 * counts from Sunday, so the shift is explicit rather than a subtraction
 * somebody has to work out twice.
 */
export const weekOf = (date: Date): Date[] => {
  const start = atMidnight(date);
  // Sunday (0) is the last day of the week being shown, not the first.
  const fromMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - fromMonday);

  return Array.from({ length: WEEK_LENGTH }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
};

/** "Mon" — the weekday, as the strip's first line writes it. */
export const weekdayShort = (date: Date): string => DAYS_SHORT[date.getDay()];

/** "23 Sep" — the day, as the strip writes every day but the chosen one. */
export const dayShort = (date: Date): string =>
  `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;

/**
 * "26 Sep, 2026" — the chosen day, which carries its year.
 *
 * Only the chosen one does. A strip of seven dates all carrying 2026 says the
 * year seven times to answer a question nobody asked; the one that is selected
 * is the one being read as a date rather than as a position in the week, and
 * it is also the one the empty state quotes back.
 */
export const dayLong = (date: Date): string =>
  `${dayShort(date)}, ${date.getFullYear()}`;


/**
 * What one appointment is doing on the day being looked at.
 *
 * Six words rather than `appointmentState`'s five, and the extra two are the
 * whole reason this screen exists. A client's history only has to say whether
 * an appointment is still coming; a day has to say which one is happening
 * *now* and which one is up *next*, because that is what somebody reading
 * their day at eleven in the morning is reading it for.
 *
 *   `InProgress`  running right now — started, not yet finished
 *   `Next`        the soonest one still ahead, and only ever one of them
 *   `Upcoming`    still ahead, behind the next one
 *   `Awaiting`    its time is up and nobody has said what became of it
 *   `Completed`   the session took place
 *   `Cancelled`   it did not
 *
 * `Awaiting` waits for the end rather than the start, and so does the rest of
 * the app now — see `hasEnded`, which this screen's reading was folded back
 * into. On a day being read as it is lived, an appointment a minute into its
 * hour is plainly still happening, and a row that stopped offering a way in
 * and started asking how it went would be asking about something nobody has
 * finished doing.
 */
export type TaskState =
  | "InProgress"
  | "Next"
  | "Upcoming"
  | "Awaiting"
  | "Completed"
  | "Cancelled";

/**
 * The appointment that is up next: the soonest one still ahead and still
 * standing, **today**, or `undefined`.
 *
 * Its own pass over the list rather than a flag worked out row by row, because
 * "next" is a fact about the day and not about the appointment — only one row
 * can hold it, and a row cannot tell on its own whether it is that one.
 *
 * Today only, and that is the whole of why the day is checked here rather than
 * left to the clock comparison below. Every appointment on a day further out
 * is later than now, so without it the first row of next Friday would wear the
 * tag — and "Next" does not mean "first in this list", it means the next
 * thing the practitioner is going to do. On a day they are not living yet,
 * nothing is.
 */
export const nextUp = (
  appointments: ClientAppointment[],
  now: Date = new Date()
): ClientAppointment | undefined =>
  appointments
    .filter((appointment) => {
      if (!isStandingStatus(appointment.status)) return false;

      const start = new Date(appointment.startsAt);
      if (Number.isNaN(start.getTime())) return false;

      return isSameDay(start, now) && start.getTime() > now.getTime();
    })
    .sort(bySoonest)[0];

/**
 * @param nextId what `nextUp` answered with, passed in rather than worked out
 * here so the whole timeline agrees on one answer for one render.
 */
export const taskStateOf = (
  appointment: ClientAppointment,
  now: Date = new Date(),
  nextId?: string
): TaskState => {
  // Answered, and nothing about the clock argues with somebody who has said
  // what happened.
  if (appointment.status === "Completed") return "Completed";
  if (appointment.status === "Cancelled") return "Cancelled";

  // Running right now, for the whole of its length — which is the state this
  // screen is built around, and the only one that gets the lifted card, the
  // ringed mark and a filled Join.
  if (isInProgress(appointment, now)) return "InProgress";

  // Over, with nobody having said how it went. From the end, never the start:
  // the tick and the cross are a question about a session that has finished.
  if (hasEnded(appointment, now)) return "Awaiting";

  // A row the clock cannot place at all — no usable start — falls through
  // to here, which is the reading that offers least: it draws as something to
  // come rather than as a session nobody marked.
  return appointment.id === nextId ? "Next" : "Upcoming";
};

/** Whether the row is one the practitioner still has something to do about. */
export const isLiveState = (state: TaskState): boolean =>
  state === "InProgress" || state === "Next" || state === "Upcoming";

/** What the four tiles across the top of the day read. */
export interface TaskSummary {
  /** Everything booked on the day, whatever became of it. */
  total: number;
  /** Sessions that took place — the status, not the clock. */
  completed: number;
  /** Still standing and not yet finished, the one under way included. */
  upcoming: number;
  cancelled: number;
}

/**
 * The day in four numbers.
 *
 * They do not add up to `total`, and that is not an oversight. An appointment
 * whose time is up and that nobody has marked is in none of the three: saying
 * it completed would write a session into a client's record that may never
 * have happened, and saying it is upcoming would be plainly false. It is
 * counted in `total`, where it belongs, and the timeline below is where it is
 * answered. See `TaskState`, and `AppointmentStatus` for the longer version of
 * why this app refuses to infer "Completed" from a clock.
 */
export const taskSummary = (
  appointments: ClientAppointment[],
  now: Date = new Date()
): TaskSummary =>
  appointments.reduce<TaskSummary>(
    (totals, appointment) => {
      const state = taskStateOf(appointment, now);
      return {
        total: totals.total + 1,
        completed: totals.completed + (state === "Completed" ? 1 : 0),
        upcoming:
          totals.upcoming +
          (state === "InProgress" || state === "Next" || state === "Upcoming"
            ? 1
            : 0),
        cancelled: totals.cancelled + (state === "Cancelled" ? 1 : 0),
      };
    },
    { total: 0, completed: 0, upcoming: 0, cancelled: 0 }
  );
